import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Container } from 'react-bootstrap';
import PortalLogo from '../../assets/portalLogo';
import { buildEndSessionUrl } from './client';
import { getEnv } from '../../getEnv';
import {
  resetAuthStore,
  getAuthStore,
  resetSessionStore,
  getRefreshToken,
  getLogoutCsrf,
  getClaims,
} from './sessionStore';
import { logSignoutManually } from './siemReporterMutations';
import { logger } from '../../lib/logger';

export const endKeycloakSession = async (reason) => {
  const refresh_token = getRefreshToken();
  const env = getEnv();
  const expected_state = getLogoutCsrf();
  const claims = getClaims() ?? {};
  const { preferred_username } = claims;
  const token = getAuthStore()?.access_token;
  const session_state = claims.sid ?? claims.session_state;
  const logoutBase = env.graphql_endpoint.replace(/\/graphql$/, '');
  
  try {
    await logSignoutManually(reason);
  } catch (err) {
    logger.error('SIEM log failed during logout');
  }

  try {
    if (reason === 'superseded session' || reason === 'duplicate') {
      // Only terminate this specific session in Keycloak —
      // leaves the original browser's session untouched
      const r = await fetch(`${logoutBase}/logout/session`, {
        method:    'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username: preferred_username, session_state }),
      });

      if (!r.ok) logger.error(`Superseded-session logout failed: ${r.status}`);

      resetAuthStore();
      resetSessionStore();
      sessionStorage.setItem('signout_reason', 'duplicate');
      window.location.replace('/signout');
      return;

    } else {
      await fetch(`${logoutBase}/logout`, {
        method:    'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username: preferred_username, session_state }),
      });
    }
  } catch (e) {
    logger.error('Failed to clear server cache on logout');
  }

  try {
    const revokeUrl = `/auth/realms/${env.keycloak_realm}/protocol/openid-connect/revoke`;
    await fetch(revokeUrl, {
      method:    'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token:            refresh_token,
        token_type_hint:  'refresh_token',
        client_id:        env.oauth_client_id,
      }),
    });
  } catch (err) {
    logger.error('Token revocation failed');
  }

  const REASON_MAP = {
    'idle timeout':       'timeout',
    'superseded session': 'duplicate',
    'duplicate':          'duplicate',
  };
  const signout_reason = REASON_MAP[reason] ?? 'signout';
  sessionStorage.setItem('signout_reason', signout_reason);
  sessionStorage.setItem('unauthenticated', 'true');
  window.dispatchEvent(new Event('session-terminated'));
  resetAuthStore();
  resetSessionStore();

  try {
    const url = await buildEndSessionUrl({
      post_logout_redirect_uri: env.portal + '/signout',
      state: expected_state,
    });
    window.location.replace(url);
  } catch (err) {
    logger.error('Failed to build end session URL');
    window.location.replace('/signout');
  }
};

export const SignOut = () => {
  const [exitState] = useState(() => {
    return sessionStorage.getItem('signout_reason') ?? 'signout';
  });
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    sessionStorage.removeItem('unauthenticated');

    const params = new URLSearchParams(window.location.search);
    const returnedState = params.get('state');
    const expectedCsrf = getLogoutCsrf();
    sessionStorage.removeItem('logout_csrf');

    if (returnedState && expectedCsrf && returnedState !== expectedCsrf) {
      logger.error('CSRF validation failed on logout callback');
    }

    sessionStorage.removeItem('signout_reason');
    resetAuthStore();
    resetSessionStore();
  }, []);

  return (
    <Container fluid className="signin" data-testid="signout">
      <Row>
        <Col>
          <Row className="py-5">
            <Col className="text-center">
              <PortalLogo className="login-logo mx-auto" />

              <div className="login-links">
                {exitState === 'duplicate' &&
                  <>
                    <p>Please wait, you are being signed out.</p>
                    <p>Reason: Detected an already active session for this user.</p>
                    <p>Only one session per user is permitted. Sign out from all other locations before attempting again to sign in here.</p>
                  </>
                }
                {exitState === 'timeout' &&
                  <>
                    <p>Your session has timed out.</p>
                    <p><Link data-testid="signin-again" to={'/signin'}>Sign In</Link> to continue.</p>
                  </>
                }
                {exitState !== 'timeout' && exitState !== 'duplicate' &&
                  <>
                    <p>You are signed out.</p>
                    <p><Link data-testid="signin-again" to={'/signin'}>Click here</Link> to sign in.</p>
                  </>
                }
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};
